import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  Users, Trophy, Star, TrendingUp, Target, Sparkles,
  Award, Heart, Zap, RefreshCw, CheckCircle2, Circle,
  ArrowUp, ArrowRight, BookOpen, BarChart2, Lightbulb,
  Flame, Sprout
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping
const iconMap = {
  trophy: Trophy,
  star: Star,
  'trending-up': TrendingUp,
  target: Target,
  seedling: Sprout,
  play: Zap,
  repeat: RefreshCw,
  award: Award
};

export default function PeerInsights() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('faheem_token');

  useEffect(() => {
    if (token) {
      loadInsights();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadInsights = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/peer-insights/analyze?access_token=${token}`
      );
      if (response.data) {
        setInsights(response.data);
      }
    } catch (error) {
      console.error('Failed to load peer insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPercentileIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Star;
    return IconComponent;
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'needs_attention':
        return <Target className="w-4 h-4 text-amber-500" />;
      default:
        return <ArrowRight className="w-4 h-4 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="peer-insights-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-500">Analyzing your progress...</p>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <Card className="bg-white border-slate-200" data-testid="peer-insights-empty">
        <CardContent className="py-16 text-center">
          <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Start Your Journey</h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Take some quizzes to see how you're progressing alongside your peers!
          </p>
        </CardContent>
      </Card>
    );
  }

  const PercentileIcon = getPercentileIcon(insights.overall_percentile?.icon);

  return (
    <div className="space-y-6" data-testid="peer-insights-container">
      {/* Header Card - Overall Standing */}
      <Card className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 border-0 shadow-xl overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${insights.overall_percentile?.color}30` }}
              >
                <PercentileIcon 
                  className="w-8 h-8" 
                  style={{ color: insights.overall_percentile?.color }}
                />
              </div>
              <div>
                <p className="text-slate-400 text-sm mb-1">Your Standing</p>
                <h2 className="text-2xl font-bold text-white mb-1">
                  {insights.overall_percentile?.label}
                </h2>
                <p className="text-slate-300 text-sm">
                  {insights.overall_percentile?.message}
                </p>
              </div>
            </div>
            <Button 
              onClick={loadInsights} 
              variant="outline" 
              size="sm"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="refresh-peer-insights-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <Trophy className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{insights.personal_best_score}%</p>
              <p className="text-xs text-slate-400">Personal Best</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <TrendingUp className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">+{insights.improvement_this_month}%</p>
              <p className="text-xs text-slate-400">This Month</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <Users className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{insights.total_peers_in_courses}</p>
              <p className="text-xs text-slate-400">Learning Together</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <Award className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">
                {insights.milestones?.filter(m => m.achieved).length || 0}/{insights.milestones?.length || 5}
              </p>
              <p className="text-xs text-slate-400">Milestones</p>
            </div>
          </div>

          {/* Encouragement Message */}
          <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
            <div className="flex items-center gap-3">
              <Heart className="w-5 h-5 text-pink-400 flex-shrink-0" />
              <p className="text-white/90 text-sm">{insights.encouragement_message}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Course Comparisons */}
      {insights.course_comparisons?.length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-blue-600" />
              </div>
              Course Progress
            </CardTitle>
            <CardDescription>How you're doing in each course compared to class average</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {insights.course_comparisons.map((course) => {
                const CourseIcon = getPercentileIcon(course.percentile_range?.icon);
                return (
                  <div key={course.course_id} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${course.percentile_range?.color}20` }}
                        >
                          <CourseIcon 
                            className="w-5 h-5" 
                            style={{ color: course.percentile_range?.color }}
                          />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-800">{course.course_name}</h4>
                          <p className="text-xs text-slate-500">
                            {course.your_quizzes_taken} quizzes completed
                          </p>
                        </div>
                      </div>
                      <Badge 
                        className="text-white"
                        style={{ backgroundColor: course.percentile_range?.color }}
                      >
                        {course.percentile_range?.label}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Your Average</span>
                        <span className="font-bold text-slate-900">{course.your_score}%</span>
                      </div>
                      <div className="relative h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="absolute h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${course.your_score}%`,
                            backgroundColor: course.percentile_range?.color 
                          }}
                        />
                        {/* Class average marker */}
                        <div 
                          className="absolute top-0 h-full w-0.5 bg-slate-600"
                          style={{ left: `${course.class_average}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>Class Average: {course.class_average}%</span>
                        {course.improvement_from_start > 0 && (
                          <span className="text-green-600 flex items-center gap-1">
                            <ArrowUp className="w-3 h-3" />
                            +{course.improvement_from_start}% growth
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Topics Where You Shine */}
        {insights.top_topics?.length > 0 && (
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Star className="w-4 h-4 text-green-600" />
                </div>
                Topics Where You Shine
              </CardTitle>
              <CardDescription>Your strongest areas - great work!</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {insights.top_topics.map((topic, idx) => (
                  <div 
                    key={topic.topic_id} 
                    className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-100"
                  >
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{topic.topic_name}</p>
                      <p className="text-xs text-slate-500">{topic.course_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{topic.your_score}%</p>
                      <p className="text-xs text-slate-500">vs {topic.class_average}% avg</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Growth Opportunities */}
        {insights.growth_opportunities?.length > 0 && (
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Lightbulb className="w-4 h-4 text-amber-600" />
                </div>
                Growth Opportunities
              </CardTitle>
              <CardDescription>Focus here to level up your skills</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {insights.growth_opportunities.map((topic) => (
                  <div 
                    key={topic.topic_id} 
                    className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100"
                  >
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      {getTrendIcon(topic.trend)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{topic.topic_name}</p>
                      <p className="text-xs text-slate-500">{topic.course_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-amber-600">{topic.your_score}%</p>
                      <p className="text-xs text-slate-500">Class avg: {topic.class_average}%</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-slate-500 text-center">
                💪 Every expert was once a beginner. You've got this!
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Milestones */}
      <Card className="bg-gradient-to-r from-slate-50 to-purple-50 border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
              <Flame className="w-4 h-4 text-white" />
            </div>
            Learning Milestones
          </CardTitle>
          <CardDescription>Celebrate your achievements along the way</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {insights.milestones?.map((milestone, idx) => {
              const MilestoneIcon = iconMap[milestone.icon] || Star;
              return (
                <div 
                  key={idx}
                  className={`p-4 rounded-xl text-center transition-all ${
                    milestone.achieved 
                      ? 'bg-white border-2 border-purple-200 shadow-sm' 
                      : 'bg-slate-100 border border-slate-200 opacity-60'
                  }`}
                >
                  <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                    milestone.achieved ? 'bg-purple-100' : 'bg-slate-200'
                  }`}>
                    {milestone.achieved ? (
                      <MilestoneIcon className="w-5 h-5 text-purple-600" />
                    ) : (
                      <Circle className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <p className={`text-xs font-medium ${milestone.achieved ? 'text-purple-800' : 'text-slate-500'}`}>
                    {milestone.title}
                  </p>
                  {milestone.achieved && (
                    <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto mt-1" />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Privacy Note */}
      <div className="text-center text-xs text-slate-400 py-4">
        <p>🔒 Your individual data is never shared. Comparisons are made using anonymous class averages.</p>
      </div>
    </div>
  );
}
